/**
 * src/utils/fileBlobStorage.js
 * Lightweight, robust IndexedDB storage for caching uploaded local files across page reloads.
 */

const DB_NAME = 'SKOPreview_DB'
const STORE_NAME = 'uploaded_blobs'
const DB_VERSION = 1

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB is not supported in this environment'))
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Saves an uploaded File or Blob to IndexedDB.
 * @param {File|Blob} file
 * @param {string} [key='current_upload']
 * @returns {Promise<boolean>}
 */
export async function saveUploadedFileToDb(file, key = 'current_upload') {
  if (!file) return false
  try {
    const db = await openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const record = {
        id: key,
        file,
        name: file.name || 'uploaded_file',
        type: file.type || '',
        size: file.size || 0,
        lastModified: file.lastModified || Date.now(),
        savedAt: Date.now()
      }
      const req = store.put(record)
      req.onsuccess = () => resolve(true)
      req.onerror = () => reject(req.error)
    })
  } catch (err) {
    console.warn('[fileBlobStorage] Failed to save file:', err)
    return false
  }
}

/**
 * Retrieves the stored File/Blob from IndexedDB.
 * @param {string} [key='current_upload']
 * @returns {Promise<{ id: string, file: File|Blob, name: string, type: string, size: number, lastModified: number, savedAt: number }|null>}
 */
export async function getUploadedFileFromDb(key = 'current_upload') {
  try {
    const db = await openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const req = store.get(key)
      req.onsuccess = () => resolve(req.result || null)
      req.onerror = () => reject(req.error)
    })
  } catch (err) {
    console.warn('[fileBlobStorage] Failed to get file:', err)
    return null
  }
}

/**
 * Clears the stored File/Blob from IndexedDB.
 * @param {string} [key='current_upload']
 * @returns {Promise<boolean>}
 */
export async function clearUploadedFileFromDb(key = 'current_upload') {
  try {
    const db = await openDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const req = store.delete(key)
      req.onsuccess = () => resolve(true)
      req.onerror = () => reject(req.error)
    })
  } catch {
    return false
  }
}
